/**
 * Shared chart theme for all Technology Radar plots.
 *
 * Centralises constants, axis/title/tooltip builders, hull series helpers,
 * colour utilities, and the group palette so the three plot modules stay DRY.
 *
 * Color concept: see COLOR-CONCEPT.md in this directory.
 */

// ---------------------------------------------------------------------------
// Unit conversion
// ---------------------------------------------------------------------------

/** Backend returns Joules; multiply by this to get Watt-hours. */
export const J_TO_WH = 1 / 3600;

// ---------------------------------------------------------------------------
// Colour palette & text colours
// ---------------------------------------------------------------------------

export const TEXT_PRIMARY = '#f1f5f9';
export const TEXT_SECONDARY = '#cbd5e1';
export const TEXT_TERTIARY = '#94a3b8';

export const FONT_BODY = 'Roboto, sans-serif';
export const FONT_TITLE = 'Big John, sans-serif';

// Corporate Design palette — warm-earth-teal family (no pink, no purple)
// Derived from Batemo Style Guide + dark-mode optimisation, intensified for Ragone visibility
export const PALETTE = [
  '#ea5b0c', // Batemo Orange
  '#12a0be', // Bright Teal (intensified from #0d7c99)
  '#f0b028', // Bright Amber (intensified from #e2a630)
  '#e04838', // Vivid Red (intensified from #d4442a)
  '#9cc840', // Bright Olive (intensified from #8db848)
  '#40b88c', // Vivid Forest Teal (intensified from #3a9a6e)
  '#d48440', // Warm Sienna (intensified from #c47a3b)
  '#68b0d0', // Bright Steel Blue (intensified from #5b9ebc)
];

// Fixed chemistry → CD colour mapping
const CHEMISTRY_COLORS = {
  'NCA': '#e04838',
  'NMC (high-nickel)': '#ea5b0c',
  'NMC (low-nickel)': '#f0b028',
  'LFP': '#d48440',
  'LCO': '#68b0d0',
  'LMO': '#9cc840',
  'NMX': '#f0783a',
  'graphite': '#12a0be',
  'graphite-silicon blend': '#40b88c',
  'LTO': '#b8a060',
};

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

/** Hex colour → rgba string. */
export function _rgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Linear interpolation between two hex colours. */
export function _lerpColor(a, b, t) {
  const parse = (hex) => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
  const ca = parse(a);
  const cb = parse(b);
  const r = Math.round(ca[0] + (cb[0] - ca[0]) * t);
  const g = Math.round(ca[1] + (cb[1] - ca[1]) * t);
  const bl = Math.round(ca[2] + (cb[2] - ca[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

/**
 * Map a year string to a colour on a cool-blue → warm-orange gradient.
 */
export function _yearColor(yearStr, minYear = 2018, maxYear = 2025) {
  const y = parseInt(yearStr, 10);
  const t = Math.max(0, Math.min(1, (y - minYear) / (maxYear - minYear)));
  return _lerpColor('#5b9ebc', '#ea5b0c', t);
}

// ---------------------------------------------------------------------------
// Stable colour assignment
// ---------------------------------------------------------------------------

/**
 * Builds a stable color map so the same group always gets the same color.
 * Checks CHEMISTRY_COLORS first, then falls back to PALETTE rotation.
 */
export function assignColors(groups) {
  const colorMap = new Map();
  const sorted = [...groups].sort();
  let paletteIdx = 0;
  sorted.forEach((g) => {
    if (CHEMISTRY_COLORS[g]) {
      colorMap.set(g, CHEMISTRY_COLORS[g]);
    } else {
      colorMap.set(g, PALETTE[paletteIdx % PALETTE.length]);
      paletteIdx++;
    }
  });
  return colorMap;
}

const _groupColorMap = {};
let _groupColorIdx = 0;

/** Assign a stable colour to a group string — checks CHEMISTRY_COLORS first. */
export function groupColor(group) {
  if (!_groupColorMap[group]) {
    if (CHEMISTRY_COLORS[group]) {
      _groupColorMap[group] = CHEMISTRY_COLORS[group];
    } else {
      _groupColorMap[group] = PALETTE[_groupColorIdx % PALETTE.length];
      _groupColorIdx++;
    }
  }
  return _groupColorMap[group];
}

// ---------------------------------------------------------------------------
// Scatter styling helper — Cell Explorer standard (clean, no glow)
// ---------------------------------------------------------------------------

/** Clean scatter style: subtle white border, no shadow in normal state. */
export function _scatterStyle(hex) {
  return {
    color: hex,
    opacity: 0.85,
    borderColor: 'rgba(255,255,255,0.2)',
    borderWidth: 1,
  };
}

// ---------------------------------------------------------------------------
// ECharts option builders
// ---------------------------------------------------------------------------

export function _baseTitle(text) {
  return {
    text,
    left: 'center',
    top: 12,
    textStyle: {
      color: TEXT_PRIMARY,
      fontFamily: FONT_TITLE,
      fontSize: 16,
    },
  };
}

export function _baseGrid() {
  return { left: 90, right: 30, top: 64, bottom: 60, containLabel: false };
}

/** Compact grid for small charts with less vertical space. */
export function _compactGrid() {
  return { left: 70, right: 20, top: 52, bottom: 45, containLabel: false };
}

/** Compact title for small charts. */
export function _compactTitle(text) {
  return {
    text,
    left: 'center',
    top: 6,
    textStyle: {
      color: TEXT_PRIMARY,
      fontFamily: FONT_TITLE,
      fontSize: 13,
    },
  };
}

/** Compact legend for small charts. */
export function _compactLegend(data) {
  return {
    data,
    top: 24,
    textStyle: { color: TEXT_TERTIARY, fontFamily: FONT_BODY, fontSize: 10 },
    icon: 'circle',
    itemWidth: 8,
    itemHeight: 8,
    itemGap: 12,
  };
}

export function _baseTooltip(extra = {}) {
  return {
    trigger: 'item',
    backgroundColor: 'rgba(50, 50, 50, 0.9)',
    borderWidth: 0,
    padding: [8, 12],
    textStyle: { color: '#ffffff', fontFamily: FONT_BODY, fontSize: 13 },
    ...extra,
  };
}

export function _baseAxis(label, opts = {}) {
  return {
    name: label,
    nameLocation: 'center',
    nameGap: 35,
    nameTextStyle: { color: TEXT_SECONDARY, fontFamily: FONT_BODY, fontSize: 13 },
    axisLabel: {
      color: TEXT_TERTIARY, fontFamily: FONT_BODY, fontSize: 11,
      formatter(value) {
        if (typeof value !== 'number') return value;
        if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
        if (Math.abs(value) >= 1000) return (value / 1000).toFixed(0) + 'k';
        return value;
      }
    },
    axisLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
    splitLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
    ...opts,
  };
}

export function _logYAxis(label) {
  return _baseAxis(label, {
    type: 'log',
    nameGap: 55,
    nameRotate: 90,
    axisLabel: {
      color: TEXT_TERTIARY,
      fontFamily: FONT_BODY,
      fontSize: 11,
      formatter(value) {
        if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
        if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
        return Math.round(value).toLocaleString();
      },
    },
    minorTick: { show: false },
    minorSplitLine: { show: false },
  });
}

export function _baseLegend(data) {
  return {
    data,
    top: 36,
    textStyle: { color: TEXT_TERTIARY, fontFamily: FONT_BODY, fontSize: 12 },
    icon: 'circle',
    itemWidth: 10,
    itemHeight: 10,
  };
}

// ---------------------------------------------------------------------------
// Convex hull series — "Neon Frontier" gradient design (subtle)
// ---------------------------------------------------------------------------

/**
 * Build a convex-hull polygon series with gradient fill and glow.
 *
 * @param {string} name - Series name
 * @param {Array} points - Hull coordinate pairs [[e, p], ...]
 * @param {string} color - Hex colour
 * @param {Object} [opts] - Extra series options to merge
 */
export function _hullSeries(name, points, color, opts = {}) {
  if (!points || points.length < 3) return null;
  const converted = points.map(([e, p]) => [e * J_TO_WH, p]);
  const closed = [...converted, converted[0]];
  const { lineStyle: lsOpts, areaStyle: asOpts, ...restOpts } = opts;
  return {
    name,
    type: 'line',
    data: closed,
    symbol: 'none',
    lineStyle: {
      color,
      width: 1.2,
      type: 'solid',
      cap: 'round',
      join: 'round',
      shadowBlur: 14,
      shadowColor: _rgba(color, 0.55),
      ...(lsOpts || {}),
    },
    areaStyle: {
      color: {
        type: 'linear',
        x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
          { offset: 0, color: _rgba(color, 0.10) },
          { offset: 1, color: _rgba(color, 0.01) },
        ],
      },
      ...(asOpts || {}),
    },
    emphasis: {
      focus: 'series',
      lineStyle: { width: 2, shadowBlur: 20, shadowColor: _rgba(color, 0.8) },
      areaStyle: { opacity: 1 },
    },
    blur: {
      lineStyle: { opacity: 0.08 },
      areaStyle: { opacity: 0.02 },
    },
    silent: true,
    z: 1,
    ...restOpts,
  };
}

/** Pick x/y from a scatter point based on basis, converting energy J→Wh. Standard: [energy, power]. */
export function _xy(pt, basis) {
  return basis === 'vol' ? [pt.e_vol * J_TO_WH, pt.p_vol] : [pt.e_grav * J_TO_WH, pt.p_grav];
}

/** Swapped axes: [power, energy_Wh] for Ragone plots with X=Power, Y=Energy. */
export function _xySwapped(pt, basis) {
  return basis === 'vol' ? [pt.p_vol, pt.e_vol * J_TO_WH] : [pt.p_grav, pt.e_grav * J_TO_WH];
}

/**
 * Hull series with swapped axes: X=power, Y=energy.
 * Input points are [[energy_J, power], ...] from backend.
 */
export function _hullSwappedSeries(name, points, color, opts = {}) {
  if (!points || points.length < 3) return null;
  const converted = points.map(([e, p]) => [p, e * J_TO_WH]);
  const closed = [...converted, converted[0]];
  const { lineStyle: lsOpts, areaStyle: asOpts, ...restOpts } = opts;
  return {
    name,
    type: 'line',
    data: closed,
    symbol: 'none',
    lineStyle: {
      color,
      width: 1.2,
      type: 'solid',
      cap: 'round',
      join: 'round',
      shadowBlur: 14,
      shadowColor: _rgba(color, 0.55),
      ...(lsOpts || {}),
    },
    areaStyle: {
      color: {
        type: 'linear',
        x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
          { offset: 0, color: _rgba(color, 0.10) },
          { offset: 1, color: _rgba(color, 0.01) },
        ],
      },
      ...(asOpts || {}),
    },
    emphasis: {
      focus: 'series',
      lineStyle: { width: 2, shadowBlur: 20, shadowColor: _rgba(color, 0.8) },
      areaStyle: { opacity: 1 },
    },
    blur: {
      lineStyle: { opacity: 0.08 },
      areaStyle: { opacity: 0.02 },
    },
    silent: true,
    z: 1,
    ...restOpts,
  };
}

// ---------------------------------------------------------------------------
// Animation helpers
// ---------------------------------------------------------------------------

/** Default animation settings for all radar plots. */
export const ANIMATION_DEFAULTS = {
  animation: true,
  animationDuration: 1000,
  animationEasing: 'cubicInOut',
};

/**
 * Create a staggered animationDelay function for year-grouped scatter data.
 * @param {Array} years - Sorted year strings
 * @param {number} [delayPerYear=300] - ms delay between year groups
 */
export function yearStaggerDelay(years, delayPerYear = 300) {
  const yearIndex = {};
  years.forEach((y, i) => { yearIndex[y] = i; });
  return (idx, params) => {
    const yr = params?.data?._meta?.year || params?.data?._meta?.group;
    return (yearIndex[yr] || 0) * delayPerYear;
  };
}
